<script setup lang="ts">
import axios from 'axios'
import { VDateInput } from 'vuetify/labs/VDateInput'
import { notifyError } from '@/utils/helpers'
import type { ECOption } from '@/plugins/echarts'



const TB_BASE_URL = import.meta.env.VITE_TB_URL
const theme = useTheme()
const { smAndDown } = useDisplay()
const isLoading = ref(false)

// ── ThingsBoard auth ──────────────────────────────────────────────────────────

const tbToken = ref<string | null>(null)
const tbAuthority = ref<string | null>(null)
const loginError = ref('')

async function getTbToken(): Promise<string | null> {
    if (tbToken.value) return tbToken.value
    try {
        const response = await apiOmni.post('api/tb/login')
        tbToken.value = response.data.token
        void loadTbAuthority()
        return tbToken.value
    } catch (e: unknown) {
        const err = e as { response?: { status: number; data: unknown }; message?: string }
        loginError.value = err.response
            ? `HTTP ${err.response.status}: ${JSON.stringify(err.response.data)}`
            : (err.message ?? String(e))
        notifyError(loginError.value)
        return null
    }
}

function tbHeaders(token: string): Record<string, string> {
    return {
        'X-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    }
}

async function loadTbAuthority() {
    if (!tbToken.value || tbAuthority.value) return
    try {
        const resp = await axios.get(`${TB_BASE_URL}/api/auth/user`, { headers: tbHeaders(tbToken.value) })
        tbAuthority.value = resp.data?.authority ?? null
    } catch {
        tbAuthority.value = null
    }
}

const peutRenommer = computed(() => tbAuthority.value === 'TENANT_ADMIN')

// ── Types ─────────────────────────────────────────────────────────────────────

declare interface TbDeviceId {
    id: string
    entityType: string
}

declare interface TbDevice {
    id: TbDeviceId
    name: string
    type: string
    label: string
    createdTime: number
}

declare interface TbPageData<T> {
    data: T[]
    hasNext: boolean
}

declare interface ChartPoint { ts: number; value: number }

declare interface JourPluie { jour: string; ts: number; mm: number }

declare interface S2120Latest {
    temp: number | null
    hum: number | null
    ventMs: number | null
    rafaleMs: number | null
    dirDeg: number | null
    précipCumul: number | null
    tauxPrécip: number | null
    pression: number | null
    lux: number | null
    uv: number | null
    batteriePct: number | null
    rssi: number | null
    snr: number | null
    ts: number | null
}

// ── State ─────────────────────────────────────────────────────────────────────

const DEVICE_TYPE = 'S2120'

const devices = ref<TbDevice[]>([])
const selectedDevice = ref<TbDevice | null>(null)
const activeId = ref<string | null>(null)

const latestVide = (): S2120Latest => ({
    temp: null, hum: null, ventMs: null, rafaleMs: null, dirDeg: null,
    précipCumul: null, tauxPrécip: null, pression: null, lux: null, uv: null,
    batteriePct: null, rssi: null, snr: null, ts: null,
})

const latest = ref<S2120Latest>(latestVide())
const isLoadingTelemetry = ref(false)

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDuration(ms: number): string {
    const minutes = Math.floor(ms / 60000)
    if (minutes < 60) return `${minutes} min`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours} h ${minutes % 60} min`
    const days = Math.floor(hours / 24)
    return `${days} j ${hours % 24} h`
}

const tempsDepuisDernière = computed(() =>
    latest.value.ts ? formatDuration(Date.now() - latest.value.ts) : null,
)

function isoJour(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function débutJourLocal(d: Date): number {
    const x = new Date(d)
    x.setHours(0, 0, 0, 0)
    return x.getTime()
}

const CARDINAUX = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSO', 'SO', 'OSO', 'O', 'ONO', 'NO', 'NNO']

function cardinal(deg: number): string {
    return CARDINAUX[Math.round(deg / 22.5) % 16]
}

function msVersKmh(ms: number | null): number | null {
    return ms === null ? null : ms * 3.6
}

// Saison agricole : du 1er avril à la fin mars suivante.
function débutSaisonTs(): number {
    const now = new Date()
    const année = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1
    return new Date(année, 3, 1).getTime()
}

// ── Latest telemetry ──────────────────────────────────────────────────────────

const CLÉS_LATEST = [
    'data_Temp_c', 'data_Hum', 'data_Current_wind_dir', 'data_Current_wind_speed',
    'data_Precip_cumul', 'data_Precip_rate', 'data_Pressure', 'data_Wind_gust_3s',
    'data_Light_lux', 'data_Uv_index', 'data_battery_pct', 'rssi', 'snr',
].join(',')

async function loadTelemetry(deviceId: string) {
    isLoadingTelemetry.value = true
    latest.value = latestVide()
    try {
        const token = await getTbToken()
        if (!token) return
        const url =
            `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries?keys=${CLÉS_LATEST}`
        const resp = await axios.get(url, { headers: tbHeaders(token) })
        const d = resp.data
        const num = (clé: string): number | null =>
            d?.[clé]?.[0]?.value !== undefined ? parseFloat(d[clé][0].value) : null
        latest.value = {
            temp: num('data_Temp_c'),
            hum: num('data_Hum'),
            ventMs: num('data_Current_wind_speed'),
            rafaleMs: num('data_Wind_gust_3s'),
            dirDeg: num('data_Current_wind_dir'),
            précipCumul: num('data_Precip_cumul'),
            tauxPrécip: num('data_Precip_rate'),
            pression: num('data_Pressure'),
            lux: num('data_Light_lux'),
            uv: num('data_Uv_index'),
            batteriePct: num('data_battery_pct'),
            rssi: num('rssi'),
            snr: num('snr'),
            ts: d?.data_Temp_c?.[0]?.ts ?? d?.data_Precip_cumul?.[0]?.ts ?? null,
        }
    } catch (e) {
        notifyError(e)
    } finally {
        isLoadingTelemetry.value = false
    }
}

// ── Condition courante (héro façon Apple Météo) ───────────────────────────────

const condition = computed<'pluie' | 'soleil' | 'nuageux' | 'nuit'>(() => {
    const l = latest.value
    if ((l.tauxPrécip ?? 0) > 0) return 'pluie'
    if ((l.lux ?? 0) > 30000) return 'soleil'
    if ((l.lux ?? 0) > 100) return 'nuageux'
    return 'nuit'
})

const CONDITIONS = {
    pluie: { icône: 'M9,12C9.53,12.14 9.85,12.69 9.71,13.22L8.41,18.05C8.27,18.59 7.72,18.9 7.19,18.76C6.65,18.62 6.34,18.07 6.5,17.54L7.78,12.71C7.92,12.17 8.47,11.86 9,12M13,12C13.53,12.14 13.85,12.69 13.71,13.22L11.64,20.95C11.5,21.5 10.95,21.8 10.41,21.66C9.88,21.5 9.56,20.97 9.7,20.43L11.78,12.71C11.92,12.17 12.47,11.86 13,12M17,12C17.53,12.14 17.85,12.69 17.71,13.22L16.41,18.05C16.27,18.59 15.72,18.9 15.19,18.76C14.65,18.62 14.34,18.07 14.5,17.54L15.78,12.71C15.92,12.17 16.47,11.86 17,12M17,10V9A5,5 0 0,0 12,4C9.5,4 7.45,5.82 7.06,8.19C6.73,8.07 6.37,8 6,8A3,3 0 0,0 3,11C3,12.11 3.6,13.08 4.5,13.6V13.59C5,13.87 5.14,14.5 4.87,14.96C4.59,15.43 4,15.6 3.5,15.32V15.33C2,14.47 1,12.85 1,11A5,5 0 0,1 6,6C7,3.65 9.3,2 12,2C15.43,2 18.24,4.66 18.5,8.03L19,8A4,4 0 0,1 23,12C23,13.5 22.2,14.77 21,15.46V15.46C20.5,15.73 19.91,15.57 19.63,15.09C19.36,14.61 19.5,14 20,13.72V13.73C20.6,13.39 21,12.74 21,12A2,2 0 0,0 19,10H17Z', texte: 'Pluie en cours', dégradé: 'linear-gradient(160deg, #3E5570 0%, #64748B 100%)' },
    soleil: { icône: 'M12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M12,2L14.39,5.42C13.65,5.15 12.84,5 12,5C11.16,5 10.35,5.15 9.61,5.42L12,2M3.34,7L7.5,6.65C6.9,7.16 6.36,7.78 5.94,8.5C5.5,9.24 5.25,10 5.11,10.79L3.34,7M3.36,17L5.12,13.23C5.26,14 5.53,14.78 5.95,15.5C6.37,16.24 6.91,16.86 7.5,17.37L3.36,17M20.65,7L18.88,10.79C18.74,10 18.47,9.23 18.05,8.5C17.63,7.78 17.1,7.15 16.5,6.64L20.65,7M20.64,17L16.5,17.36C17.09,16.85 17.62,16.22 18.04,15.5C18.46,14.77 18.73,14 18.87,13.21L20.64,17M12,22L9.59,18.56C10.33,18.83 11.14,19 12,19C12.82,19 13.63,18.83 14.37,18.56L12,22Z', texte: 'Ensoleillé', dégradé: 'linear-gradient(160deg, #1D6FD8 0%, #5AA7F0 100%)' },
    nuageux: { icône: 'M12.74,5.47C15.1,6.5 16.35,9.03 15.92,11.46C17.19,12.56 18,14.19 18,16V16.17C18.31,16.06 18.65,16 19,16A3,3 0 0,1 22,19A3,3 0 0,1 19,22H6A4,4 0 0,1 2,18A4,4 0 0,1 6,14H6.27C5,12.45 4.6,10.24 5.5,8.26C6.72,5.5 9.97,4.24 12.74,5.47M11.93,7.3C10.16,6.5 8.09,7.31 7.31,9.07C6.85,10.09 6.93,11.22 7.41,12.13C8.5,10.83 10.16,10 12,10C12.7,10 13.38,10.12 14,10.34C13.94,9.06 13.18,7.86 11.93,7.3M13.55,3.64C13,3.4 12.45,3.23 11.88,3.12L14.37,1.82L15.27,4.71C14.76,4.29 14.19,3.93 13.55,3.64M6.09,4.44C5.6,4.79 5.17,5.19 4.8,5.63L4.91,2.82L7.87,3.5C7.25,3.71 6.65,4.03 6.09,4.44M18,9.71C17.91,9.12 17.78,8.55 17.59,8L19.97,9.5L17.92,11.73C18.03,11.08 18.05,10.4 18,9.71M3.04,11.3C3.11,11.9 3.24,12.47 3.43,13L1.06,11.5L3.1,9.28C3,9.93 2.97,10.61 3.04,11.3M19,18H16V16A4,4 0 0,0 12,12A4,4 0 0,0 8,16H6A2,2 0 0,0 4,18A2,2 0 0,0 6,20H19A1,1 0 0,0 20,19A1,1 0 0,0 19,18Z', texte: 'Variable', dégradé: 'linear-gradient(160deg, #4A6D96 0%, #86A6C6 100%)' },
    nuit: { icône: 'M17.75,4.09L15.22,6.03L16.13,9.09L13.5,7.28L10.87,9.09L11.78,6.03L9.25,4.09L12.44,4L13.5,1L14.56,4L17.75,4.09M21.25,11L19.61,12.25L20.2,14.23L18.5,13.06L16.8,14.23L17.39,12.25L15.75,11L17.81,10.95L18.5,9L19.19,10.95L21.25,11M18.97,15.95C19.8,15.87 20.69,17.05 20.16,17.8C19.84,18.25 19.5,18.67 19.08,19.07C15.17,23 8.84,23 4.94,19.07C1.03,15.17 1.03,8.83 4.94,4.93C5.34,4.53 5.76,4.17 6.21,3.85C6.96,3.32 8.14,4.21 8.06,5.04C7.79,7.9 8.75,10.87 10.95,13.06C13.14,15.26 16.1,16.22 18.97,15.95M17.33,17.97C14.5,17.81 11.7,16.64 9.53,14.5C7.36,12.31 6.2,9.5 6.04,6.68C3.23,9.82 3.34,14.64 6.35,17.66C9.37,20.67 14.19,20.78 17.33,17.97Z', texte: 'Nuit', dégradé: 'linear-gradient(160deg, #17233B 0%, #3A4C6B 100%)' },
} as const

const héroCondition = computed(() => CONDITIONS[condition.value])

// ── Précipitations journalières ───────────────────────────────────────────────

// L'agrégation ThingsBoard expire au-delà de ~150 jours : on découpe la période en tranches.
const TRANCHE_MS = 120 * 86400000

async function fetchMaxJournalier(deviceId: string, startTs: number, endTs: number): Promise<ChartPoint[]> {
    const token = await getTbToken()
    if (!token) return []
    const points: ChartPoint[] = []
    for (let s = startTs; s < endTs; s += TRANCHE_MS) {
        const e = Math.min(s + TRANCHE_MS, endTs)
        const url =
            `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries` +
            `?keys=data_Precip_cumul&startTs=${s}&endTs=${e}&interval=86400000&agg=MAX&limit=50000`
        const resp = await axios.get(url, { headers: tbHeaders(token) })
        for (const p of resp.data?.data_Precip_cumul ?? [])
            points.push({ ts: p.ts, value: parseFloat(p.value) })
    }
    return points.sort((a, b) => a.ts - b.ts)
}

const pluieParJour = ref<JourPluie[]>([])
const pluieChargéeDepuis = ref<number | null>(null)
const isLoadingPluie = ref(false)
// Complétion de la saison en arrière-plan : ne masque pas le graphique déjà affiché.
const isLoadingSaison = ref(false)

async function loadPluie(deviceId: string, depuisTs: number, enArrièrePlan = false) {
    if (enArrièrePlan) isLoadingSaison.value = true
    else isLoadingPluie.value = true
    try {
        // Un jour de plus en amont pour disposer d'une valeur de référence du compteur.
        const startTs = débutJourLocal(new Date(depuisTs)) - 86400000
        const points = await fetchMaxJournalier(deviceId, startTs, Date.now())
        // L'appareil a pu changer pendant la requête : ne pas écraser les données de l'autre station.
        if (selectedDevice.value?.id.id !== deviceId) return
        const jours: JourPluie[] = []
        for (let i = 1; i < points.length; i++) {
            const précédent = points[i - 1].value
            const courant = points[i].value
            // Compteur cumulatif : un recul signale un redémarrage de la station.
            const mm = courant >= précédent ? courant - précédent : courant
            const jourTs = débutJourLocal(new Date(points[i].ts))
            jours.push({ jour: isoJour(new Date(jourTs)), ts: jourTs, mm: Math.round(mm * 100) / 100 })
        }
        pluieParJour.value = jours
        pluieChargéeDepuis.value = startTs + 86400000
    } catch (e) {
        notifyError(e)
    } finally {
        if (enArrièrePlan) isLoadingSaison.value = false
        else isLoadingPluie.value = false
    }
}

// 7 jours d'abord pour un premier affichage rapide, puis la saison complète en arrière-plan.
async function loadPluieInitiale(deviceId: string) {
    await loadPluie(deviceId, Date.now() - 6 * 86400000)
    if (selectedDevice.value?.id.id !== deviceId) return
    const débutSaison = débutSaisonTs()
    if (pluieChargéeDepuis.value === null || débutSaison < pluieChargéeDepuis.value)
        await loadPluie(deviceId, débutSaison, true)
}

// ── Statistiques de pluie ─────────────────────────────────────────────────────

function sommePluieDepuis(jourMin: string): number | null {
    if (pluieParJour.value.length === 0) return null
    // Données pas encore chargées aussi loin en arrière : mieux vaut un « … » qu'un cumul tronqué.
    if (pluieChargéeDepuis.value !== null && new Date(`${jourMin}T00:00:00`).getTime() < pluieChargéeDepuis.value)
        return null
    return Math.round(
        pluieParJour.value.filter(j => j.jour >= jourMin).reduce((acc, j) => acc + j.mm, 0) * 100,
    ) / 100
}

const jourAujourdhui = computed(() => isoJour(new Date()))
const jourHier = computed(() => isoJour(new Date(Date.now() - 86400000)))

const pluieAujourdhui = computed(() => {
    const j = pluieParJour.value.find(x => x.jour === jourAujourdhui.value)
    return j ? j.mm : (pluieParJour.value.length ? 0 : null)
})
const pluieHier = computed(() => {
    const j = pluieParJour.value.find(x => x.jour === jourHier.value)
    return j ? j.mm : (pluieParJour.value.length ? 0 : null)
})
const pluie7j = computed(() => sommePluieDepuis(isoJour(new Date(Date.now() - 6 * 86400000))))
const pluie30j = computed(() => sommePluieDepuis(isoJour(new Date(Date.now() - 29 * 86400000))))
const pluieSaison = computed(() => sommePluieDepuis(isoJour(new Date(débutSaisonTs()))))

declare interface TuilePluie { label: string; valeur: number | null; icône: string }

const tuilesPluie = computed<TuilePluie[]>(() => [
    { label: 'Aujourd\'hui', valeur: pluieAujourdhui.value, icône: 'M7,10H12V15H7M19,19H5V8H19M19,3H18V1H16V3H8V1H6V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3Z' },
    { label: 'Hier', valeur: pluieHier.value, icône: 'M13.5,8H12V13L16.28,15.54L17,14.33L13.5,12.25V8M13,3A9,9 0 0,0 4,12H1L4.96,16.03L9,12H6A7,7 0 0,1 13,5A7,7 0 0,1 20,12A7,7 0 0,1 13,19C11.07,19 9.32,18.21 8.06,16.94L6.64,18.36C8.27,20 10.5,21 13,21A9,9 0 0,0 22,12A9,9 0 0,0 13,3' },
    { label: '7 derniers jours', valeur: pluie7j.value, icône: 'M6 1H8V3H16V1H18V3H19C20.11 3 21 3.9 21 5V19C21 20.11 20.11 21 19 21H5C3.89 21 3 20.1 3 19V5C3 3.89 3.89 3 5 3H6V1M5 8V19H19V8H5M7 10H17V12H7V10Z' },
    { label: '30 derniers jours', valeur: pluie30j.value, icône: 'M9,10V12H7V10H9M13,10V12H11V10H13M17,10V12H15V10H17M19,3A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5C3.89,21 3,20.1 3,19V5A2,2 0 0,1 5,3H6V1H8V3H16V1H18V3H19M19,19V8H5V19H19M9,14V16H7V14H9M13,14V16H11V14H13M17,14V16H15V14H17Z' },
    { label: 'Saison (1ᵉʳ avril)', valeur: pluieSaison.value, icône: 'M2,22V20C2,20 7,18 12,18C17,18 22,20 22,20V22H2M11.3,9.1C10.1,5.2 4,6.1 4,6.1C4,6.1 4.2,13.9 9.9,12.7C9.5,9.8 8,9 8,9C10.8,9 11,12.4 11,12.4V17C11.3,17 11.7,17 12,17C12.3,17 12.7,17 13,17V12.8C13,12.8 13,8.9 16,7.9C16,7.9 14,10.9 14,12.9C21,13.6 21,4 21,4C21,4 12.1,3 11.3,9.1Z' },
])

// ── Période du graphique de précipitations ────────────────────────────────────

type PériodePluie = '7j' | '30j' | 'saison' | 'custom'

const périodePluie = ref<PériodePluie>('7j')
const startDate = ref(isoJour(new Date(Date.now() - 6 * 86400000)))
const endDate = ref(isoJour(new Date()))

function plageDepuisBornes(min: string, max: string): Date[] {
    if (!min || !max) return []
    const dates: Date[] = []
    const jour = new Date(`${min}T00:00:00`)
    const fin = new Date(`${max}T00:00:00`)
    while (jour <= fin) {
        dates.push(new Date(jour))
        jour.setDate(jour.getDate() + 1)
    }
    return dates
}

const plagePluie = computed<Date[]>({
    get: () => plageDepuisBornes(startDate.value, endDate.value),
    set: (dates) => {
        if (dates.length === 0) return
        startDate.value = isoJour(dates[0])
        endDate.value = isoJour(dates[dates.length - 1])
        périodePluie.value = 'custom'
        void assurerPluieChargée(new Date(`${startDate.value}T00:00:00`).getTime())
    },
})

function appliquerPériodePluie(mode: PériodePluie) {
    périodePluie.value = mode
    const fin = new Date()
    endDate.value = isoJour(fin)
    if (mode === '7j') startDate.value = isoJour(new Date(Date.now() - 6 * 86400000))
    else if (mode === '30j') startDate.value = isoJour(new Date(Date.now() - 29 * 86400000))
    else if (mode === 'saison') startDate.value = isoJour(new Date(débutSaisonTs()))
    void assurerPluieChargée(new Date(`${startDate.value}T00:00:00`).getTime())
}

async function assurerPluieChargée(depuisTs: number) {
    if (!selectedDevice.value) return
    if (pluieChargéeDepuis.value !== null && depuisTs >= pluieChargéeDepuis.value) return
    await loadPluie(selectedDevice.value.id.id, depuisTs)
}

function rafraîchirPluie() {
    if (!selectedDevice.value) return
    const départ = Math.min(débutSaisonTs(), new Date(`${startDate.value}T00:00:00`).getTime())
    void loadPluie(selectedDevice.value.id.id, départ)
}

const pluieAffichée = computed<JourPluie[]>(() =>
    pluieParJour.value.filter(j => j.jour >= startDate.value && j.jour <= endDate.value),
)

// ── Couleurs des graphiques ───────────────────────────────────────────────────

const COULEUR_PLUIE = '#3B82F6'
const COULEUR_CUMUL = '#F59E0B'

const estSombre = computed(() => theme.global.name.value === 'dark')
const couleurTexte = computed(() => (estSombre.value ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)'))
const couleurGrille = computed(() => (estSombre.value ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)'))

function dégradé(couleur: string, opacité: number) {
    const alpha = Math.round(opacité * 255).toString(16).padStart(2, '0')
    return {
        type: 'linear' as const,
        x: 0, y: 0, x2: 0, y2: 1,
        colorStops: [
            { offset: 0, color: `${couleur}${alpha}` },
            { offset: 1, color: `${couleur}00` },
        ],
    }
}

// ── Graphique précipitations (barres journalières + cumul) ────────────────────

const pluieChartOption = computed<ECOption>(() => {
    const jours = pluieAffichée.value
    if (jours.length === 0) return {}
    let cumul = 0
    const cumuls = jours.map(j => {
        cumul += j.mm
        return Math.round(cumul * 100) / 100
    })
    const axeCommun = {
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: couleurTexte.value, fontSize: 10 },
        nameTextStyle: { color: couleurTexte.value, fontSize: 10 },
    }
    return {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            backgroundColor: estSombre.value ? 'rgba(30,30,30,0.94)' : 'rgba(255,255,255,0.96)',
            borderWidth: 0,
            padding: [8, 12],
            textStyle: { color: estSombre.value ? '#fff' : '#000', fontSize: 12 },
            formatter(params: unknown) {
                const items = params as Array<{ seriesName: string; value: number; marker: string; name: string }>
                if (!items.length) return ''
                const date = new Date(`${items[0].name}T00:00:00`)
                    .toLocaleDateString('fr-CA', { weekday: 'short', day: 'numeric', month: 'short' })
                let html = `<div style="opacity:.7;margin-bottom:4px;">${date}</div>`
                for (const p of items) {
                    const unit = ' mm'
                    html += `${p.marker}${p.seriesName} <b style="float:right;margin-left:16px;">${p.value.toFixed(1)}${unit}</b><br/>`
                }
                return html
            },
        },
        legend: {
            top: 0,
            right: 8,
            icon: 'roundRect',
            itemWidth: 14,
            itemHeight: 4,
            textStyle: { color: couleurTexte.value, fontSize: 11 },
        },
        grid: { top: 34, left: 8, right: 8, bottom: 4, containLabel: true },
        xAxis: {
            type: 'category',
            data: jours.map(j => j.jour),
            ...axeCommun,
            axisLabel: {
                color: couleurTexte.value,
                fontSize: 10,
                formatter: (v: string) =>
                    new Date(`${v}T00:00:00`).toLocaleDateString('fr-CA', { day: 'numeric', month: 'short' }),
            },
        },
        yAxis: [
            {
                type: 'value',
                name: 'mm / jour',
                position: 'left',
                ...axeCommun,
                splitLine: { show: true, lineStyle: { color: couleurGrille.value } },
            },
            {
                type: 'value',
                name: 'cumul (mm)',
                position: 'right',
                ...axeCommun,
                splitLine: { show: false },
            },
        ],
        dataZoom: [{ type: 'inside', zoomOnMouseWheel: 'shift', moveOnMouseWheel: false }],
        series: [
            {
                name: 'Pluie',
                type: 'bar',
                yAxisIndex: 0,
                data: jours.map(j => j.mm),
                barMaxWidth: 26,
                itemStyle: {
                    borderRadius: [4, 4, 0, 0],
                    color: {
                        type: 'linear' as const,
                        x: 0, y: 0, x2: 0, y2: 1,
                        colorStops: [
                            { offset: 0, color: COULEUR_PLUIE },
                            { offset: 1, color: `${COULEUR_PLUIE}88` },
                        ],
                    },
                },
            },
            {
                name: 'Cumul',
                type: 'line',
                yAxisIndex: 1,
                data: cumuls,
                showSymbol: false,
                smooth: 0.2,
                lineStyle: { width: 2, color: COULEUR_CUMUL },
                itemStyle: { color: COULEUR_CUMUL },
            },
        ],
    }
})

// ── Tuiles de mesures + graphique au clic ─────────────────────────────────────

type MétriqueId = 'temp' | 'hum' | 'vent' | 'rafale' | 'pression' | 'lumiere' | 'uv' | 'batterie'

declare interface Métrique {
    id: MétriqueId
    clé: string
    label: string
    unité: string
    icône: string
    couleur: string
    décimales: number
    facteur: number
}

const MÉTRIQUES: Métrique[] = [
    { id: 'temp', clé: 'data_Temp_c', label: 'Température', unité: '°C', icône: 'M15 13V5A3 3 0 0 0 9 5V13A5 5 0 1 0 15 13M12 4A1 1 0 0 1 13 5V8H11V5A1 1 0 0 1 12 4Z', couleur: '#F97316', décimales: 1, facteur: 1 },
    { id: 'hum', clé: 'data_Hum', label: 'Humidité', unité: '%', icône: 'M12,3.25C12,3.25 6,10 6,14C6,17.32 8.69,20 12,20A6,6 0 0,0 18,14C18,10 12,3.25 12,3.25M14.47,9.97L15.53,11.03L9.53,17.03L8.47,15.97M9.75,10A1.25,1.25 0 0,1 11,11.25A1.25,1.25 0 0,1 9.75,12.5A1.25,1.25 0 0,1 8.5,11.25A1.25,1.25 0 0,1 9.75,10M14.25,14.5A1.25,1.25 0 0,1 15.5,15.75A1.25,1.25 0 0,1 14.25,17A1.25,1.25 0 0,1 13,15.75A1.25,1.25 0 0,1 14.25,14.5Z', couleur: '#38BDF8', décimales: 0, facteur: 1 },
    { id: 'vent', clé: 'data_Current_wind_speed', label: 'Vent', unité: 'km/h', icône: 'M4,10A1,1 0 0,1 3,9A1,1 0 0,1 4,8H12A2,2 0 0,0 14,6A2,2 0 0,0 12,4C11.45,4 10.95,4.22 10.59,4.59C10.2,5 9.56,5 9.17,4.59C8.78,4.2 8.78,3.56 9.17,3.17C9.9,2.45 10.9,2 12,2A4,4 0 0,1 16,6A4,4 0 0,1 12,10H4M19,12A1,1 0 0,0 20,11A1,1 0 0,0 19,10C18.72,10 18.47,10.11 18.29,10.29C17.9,10.68 17.27,10.68 16.88,10.29C16.5,9.9 16.5,9.27 16.88,8.88C17.42,8.34 18.17,8 19,8A3,3 0 0,1 22,11A3,3 0 0,1 19,14H5A1,1 0 0,1 4,13A1,1 0 0,1 5,12H19M18,18H4A1,1 0 0,1 3,17A1,1 0 0,1 4,16H18A3,3 0 0,1 21,19A3,3 0 0,1 18,22C17.17,22 16.42,21.66 15.88,21.12C15.5,20.73 15.5,20.1 15.88,19.71C16.27,19.32 16.9,19.32 17.29,19.71C17.47,19.89 17.72,20 18,20A1,1 0 0,0 19,19A1,1 0 0,0 18,18Z', couleur: '#10B981', décimales: 1, facteur: 3.6 },
    { id: 'rafale', clé: 'data_Wind_gust_3s', label: 'Rafales', unité: 'km/h', icône: 'M3 5C3 4.4 3.4 4 4 4H5C5.6 4 6 4.4 6 5S5.6 6 5 6H4C3.4 6 3 5.6 3 5M4 13C4 12.4 4.4 12 5 12H6C6.6 12 7 12.4 7 13S6.6 14 6 14H5C4.4 14 4 13.6 4 13M4 16C3.4 16 3 16.4 3 17S3.4 18 4 18H9C9.6 18 10 17.6 10 17S9.6 16 9 16H4M18 5C18 4.4 18.4 4 19 4H21C21.6 4 22 4.4 22 5S21.6 6 21 6H19C18.4 6 18 5.6 18 5M7 20C6.4 20 6 20.4 6 21S6.4 22 7 22H11C11.6 22 12 21.6 12 21S11.6 20 11 20H7M3 10C2.4 10 2 9.6 2 9S2.4 8 3 8H12C13.1 8 14 7.1 14 6S13.1 4 12 4C11.4 4 10.9 4.2 10.6 4.6C10.2 5 9.6 5 9.2 4.6C8.8 4.2 8.8 3.6 9.2 3.2C9.9 2.5 10.9 2 12 2C14.2 2 16 3.8 16 6S14.2 10 12 10H3M19 12C19.6 12 20 11.6 20 11S19.6 10 19 10C18.7 10 18.5 10.1 18.3 10.3C17.9 10.7 17.3 10.7 16.9 10.3C16.5 9.9 16.5 9.3 16.9 8.9C17.4 8.3 18.2 8 19 8C20.7 8 22 9.3 22 11S20.7 14 19 14H10C9.4 14 9 13.6 9 13S9.4 12 10 12H19M18 18H13C12.4 18 12 17.6 12 17S12.4 16 13 16H18C19.7 16 21 17.3 21 19S19.7 22 18 22C17.2 22 16.4 21.7 15.9 21.1C15.5 20.7 15.5 20.1 15.9 19.7C16.3 19.3 16.9 19.3 17.3 19.7C17.5 19.9 17.7 20 18 20C18.6 20 19 19.6 19 19S18.6 18 18 18Z', couleur: '#14B8A6', décimales: 1, facteur: 3.6 },
    { id: 'pression', clé: 'data_Pressure', label: 'Pression', unité: 'hPa', icône: 'M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12C20,14.4 19,16.5 17.3,18C15.9,16.7 14,16 12,16C10,16 8.2,16.7 6.7,18C5,16.5 4,14.4 4,12A8,8 0 0,1 12,4M14,5.89C13.62,5.9 13.26,6.15 13.1,6.54L11.81,9.77L11.71,10C11,10.13 10.41,10.6 10.14,11.26C9.73,12.29 10.23,13.45 11.26,13.86C12.29,14.27 13.45,13.77 13.86,12.74C14.12,12.08 14,11.32 13.57,10.76L13.67,10.5L14.96,7.29L14.97,7.26C15.17,6.75 14.92,6.17 14.41,5.96C14.28,5.91 14.15,5.89 14,5.89M10,6A1,1 0 0,0 9,7A1,1 0 0,0 10,8A1,1 0 0,0 11,7A1,1 0 0,0 10,6M7,9A1,1 0 0,0 6,10A1,1 0 0,0 7,11A1,1 0 0,0 8,10A1,1 0 0,0 7,9M17,9A1,1 0 0,0 16,10A1,1 0 0,0 17,11A1,1 0 0,0 18,10A1,1 0 0,0 17,9Z', couleur: '#8B5CF6', décimales: 1, facteur: 1 },
    { id: 'lumiere', clé: 'data_Light_lux', label: 'Luminosité', unité: 'lux', icône: 'M3.55 19.09L4.96 20.5L6.76 18.71L5.34 17.29M12 6C8.69 6 6 8.69 6 12S8.69 18 12 18 18 15.31 18 12C18 8.68 15.31 6 12 6M20 13H23V11H20M17.24 18.71L19.04 20.5L20.45 19.09L18.66 17.29M20.45 5L19.04 3.6L17.24 5.39L18.66 6.81M13 1H11V4H13M6.76 5.39L4.96 3.6L3.55 5L5.34 6.81L6.76 5.39M1 13H4V11H1M13 20H11V23H13', couleur: '#FBBF24', décimales: 0, facteur: 1 },
    { id: 'uv', clé: 'data_Uv_index', label: 'Indice UV', unité: '', icône: 'M7,17H4C2.38,17 0.96,15.74 0.76,14.14L0.26,11.15C0.15,10.3 0.39,9.5 0.91,8.92C1.43,8.34 2.19,8 3,8H9C9.83,8 10.58,8.35 11.06,8.96C11.17,9.11 11.27,9.27 11.35,9.45C11.78,9.36 12.22,9.36 12.64,9.45C12.72,9.27 12.82,9.11 12.94,8.96C13.41,8.35 14.16,8 15,8H21C21.81,8 22.57,8.34 23.09,8.92C23.6,9.5 23.84,10.3 23.74,11.11L23.23,14.18C23.04,15.74 21.61,17 20,17H17C15.44,17 13.92,15.81 13.54,14.3L12.64,11.59C12.26,11.31 11.73,11.31 11.35,11.59L10.43,14.37C10.07,15.82 8.56,17 7,17Z', couleur: '#E879F9', décimales: 1, facteur: 1 },
    { id: 'batterie', clé: 'data_battery_pct', label: 'Batterie', unité: '%', icône: 'M16 20H8V6H16M16.67 4H15V2H9V4H7.33C6.6 4 6 4.6 6 5.33V20.67C6 21.4 6.6 22 7.33 22H16.67C17.41 22 18 21.41 18 20.67V5.33C18 4.6 17.4 4 16.67 4M15 16H9V19H15V16M15 7H9V10H15V7M15 11.5H9V14.5H15V11.5Z', couleur: '#4CAF50', décimales: 0, facteur: 1 },
]

function valeurMétrique(id: MétriqueId): number | null {
    const l = latest.value
    switch (id) {
        case 'temp': return l.temp
        case 'hum': return l.hum
        case 'vent': return msVersKmh(l.ventMs)
        case 'rafale': return msVersKmh(l.rafaleMs)
        case 'pression': return l.pression
        case 'lumiere': return l.lux
        case 'uv': return l.uv
        case 'batterie': return l.batteriePct
    }
}

const métriqueSélectionnée = ref<MétriqueId | null>(null)
const métriquePériodeJours = ref<1 | 7 | 30>(7)
const métriquePoints = ref<ChartPoint[]>([])
const isLoadingMétrique = ref(false)

const métriqueMeta = computed<Métrique | null>(() =>
    MÉTRIQUES.find(m => m.id === métriqueSélectionnée.value) ?? null,
)

function surClicTuile(id: MétriqueId) {
    if (métriqueSélectionnée.value === id) {
        métriqueSélectionnée.value = null
        return
    }
    métriqueSélectionnée.value = id
    void loadMétrique()
}

function appliquerPériodeMétrique(jours: 1 | 7 | 30) {
    métriquePériodeJours.value = jours
    void loadMétrique()
}

async function loadMétrique() {
    const device = selectedDevice.value
    const meta = métriqueMeta.value
    if (!device || !meta) return
    isLoadingMétrique.value = true
    métriquePoints.value = []
    try {
        const token = await getTbToken()
        if (!token) return
        const endTs = Date.now()
        const startTs = endTs - métriquePériodeJours.value * 86400000
        const url =
            `${TB_BASE_URL}/api/plugins/telemetry/DEVICE/${device.id.id}/values/timeseries` +
            `?keys=${meta.clé}&startTs=${startTs}&endTs=${endTs}&limit=50000&orderBy=ASC`
        const resp = await axios.get(url, { headers: tbHeaders(token) })
        métriquePoints.value = (resp.data?.[meta.clé] ?? []).map((p: { ts: number; value: string }) => ({
            ts: p.ts,
            value: parseFloat(p.value) * meta.facteur,
        }))
    } catch (e) {
        notifyError(e)
    } finally {
        isLoadingMétrique.value = false
    }
}

const métriqueChartOption = computed<ECOption>(() => {
    const meta = métriqueMeta.value
    if (!meta || métriquePoints.value.length === 0) return {}
    const axeCommun = {
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: couleurTexte.value, fontSize: 10 },
        nameTextStyle: { color: couleurTexte.value, fontSize: 10 },
    }
    return {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'line', lineStyle: { color: couleurTexte.value, type: 'dashed' } },
            backgroundColor: estSombre.value ? 'rgba(30,30,30,0.94)' : 'rgba(255,255,255,0.96)',
            borderWidth: 0,
            padding: [8, 12],
            textStyle: { color: estSombre.value ? '#fff' : '#000', fontSize: 12 },
            formatter(params: unknown) {
                const items = params as Array<{ value: [number, number]; marker: string }>
                if (!items.length) return ''
                const date = new Date(items[0].value[0])
                    .toLocaleString('fr-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                return `<div style="opacity:.7;margin-bottom:4px;">${date}</div>` +
                    `${items[0].marker}${meta.label} <b style="float:right;margin-left:16px;">` +
                    `${items[0].value[1].toFixed(meta.décimales)} ${meta.unité}</b>`
            },
        },
        grid: { top: 24, left: 8, right: 8, bottom: 4, containLabel: true },
        xAxis: {
            type: 'time',
            ...axeCommun,
            splitLine: { show: true, lineStyle: { color: couleurGrille.value, type: 'dashed' } },
        },
        yAxis: {
            type: 'value',
            name: meta.unité,
            scale: true,
            ...axeCommun,
            splitLine: { show: true, lineStyle: { color: couleurGrille.value } },
        },
        dataZoom: [{ type: 'inside', zoomOnMouseWheel: 'shift', moveOnMouseWheel: false }],
        series: [{
            name: meta.label,
            type: 'line',
            showSymbol: false,
            smooth: 0.2,
            data: métriquePoints.value.map(p => [p.ts, p.value]),
            lineStyle: { width: 2, color: meta.couleur },
            itemStyle: { color: meta.couleur },
            areaStyle: { color: dégradé(meta.couleur, 0.28) },
        }],
    }
})

// ── Sélection et chargement ───────────────────────────────────────────────────

function onSelectDevice(device: TbDevice) {
    activeId.value = device.id.id
    selectedDevice.value = device
    latest.value = latestVide()
    pluieParJour.value = []
    pluieChargéeDepuis.value = null
    métriqueSélectionnée.value = null
    métriquePoints.value = []
    void loadTelemetry(device.id.id)
    void loadPluieInitiale(device.id.id)
}

async function fetchAllDevices(token: string): Promise<TbDevice[]> {
    const PAGE_SIZE = 100
    let page = 0
    const result: TbDevice[] = []
    while (true) {
        const url =
            `${TB_BASE_URL}/api/tenant/devices` +
            `?pageSize=${PAGE_SIZE}&page=${page}&type=${encodeURIComponent(DEVICE_TYPE)}`
        const response = await axios.get<TbPageData<TbDevice>>(url, { headers: tbHeaders(token) })
        result.push(...response.data.data)
        if (!response.data.hasNext) break
        page++
    }
    return result.sort((a, b) => (a.label || a.name).localeCompare(b.label || b.name))
}

async function loadDevices() {
    loginError.value = ''
    isLoading.value = true
    try {
        const token = await getTbToken()
        if (!token) return
        devices.value = await fetchAllDevices(token)
        const courant = devices.value.find(d => d.id.id === activeId.value)
        if (courant) {
            onSelectDevice(courant)
        } else if (devices.value.length > 0) {
            onSelectDevice(devices.value[0])
        }
    } catch (e) {
        notifyError(e)
    } finally {
        isLoading.value = false
    }
}

onMounted(loadDevices)

// ── Renommer une station ──────────────────────────────────────────────────────

const renameDialog = ref(false)
const renameLabel = ref('')
const isSavingLabel = ref(false)

function openRenameDialog() {
    if (!selectedDevice.value) return
    renameLabel.value = selectedDevice.value.label || ''
    renameDialog.value = true
}

async function saveLabel() {
    const device = selectedDevice.value
    if (!device) return
    const label = renameLabel.value.trim()
    isSavingLabel.value = true
    try {
        const token = await getTbToken()
        if (!token) return
        // ThingsBoard remplace l'entité complète : on repart de la version courante du serveur.
        const current = await axios.get<TbDevice>(
            `${TB_BASE_URL}/api/device/${device.id.id}`,
            { headers: tbHeaders(token) },
        )
        const resp = await axios.post<TbDevice>(
            `${TB_BASE_URL}/api/device`,
            { ...current.data, label },
            { headers: tbHeaders(token) },
        )
        device.label = resp.data.label
        renameDialog.value = false
        Notify.success('Nom de la station modifié avec succès')
    } catch (e) {
        notifyError(e)
    } finally {
        isSavingLabel.value = false
    }
}
</script>

<template>
    <v-card class="page-card" :loading="isLoading">

        <!-- Page header -->
        <!-- Sur mobile il n'y a pas de v-app-bar : on décale à droite pour libérer le hamburger flottant. -->
        <div class="page-header" :class="smAndDown ? 'ps-16 pe-3 py-2' : 'px-5 pt-4 pb-3'">
            <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
                <div class="d-flex align-center" style="gap: 12px; min-width: 0;">
                    <v-avatar v-if="!smAndDown" color="primary" variant="tonal" size="44" rounded="lg">
                        <v-icon icon="M12.74,5.47C15.1,6.5 16.35,9.03 15.92,11.46C17.19,12.56 18,14.19 18,16V16.17C18.31,16.06 18.65,16 19,16A3,3 0 0,1 22,19A3,3 0 0,1 19,22H6A4,4 0 0,1 2,18A4,4 0 0,1 6,14H6.27C5,12.45 4.6,10.24 5.5,8.26C6.72,5.5 9.97,4.24 12.74,5.47M11.93,7.3C10.16,6.5 8.09,7.31 7.31,9.07C6.85,10.09 6.93,11.22 7.41,12.13C8.5,10.83 10.16,10 12,10C12.7,10 13.38,10.12 14,10.34C13.94,9.06 13.18,7.86 11.93,7.3M13.55,3.64C13,3.4 12.45,3.23 11.88,3.12L14.37,1.82L15.27,4.71C14.76,4.29 14.19,3.93 13.55,3.64M6.09,4.44C5.6,4.79 5.17,5.19 4.8,5.63L4.91,2.82L7.87,3.5C7.25,3.71 6.65,4.03 6.09,4.44M18,9.71C17.91,9.12 17.78,8.55 17.59,8L19.97,9.5L17.92,11.73C18.03,11.08 18.05,10.4 18,9.71M3.04,11.3C3.11,11.9 3.24,12.47 3.43,13L1.06,11.5L3.1,9.28C3,9.93 2.97,10.61 3.04,11.3M19,18H16V16A4,4 0 0,0 12,12A4,4 0 0,0 8,16H6A2,2 0 0,0 4,18A2,2 0 0,0 6,20H19A1,1 0 0,0 20,19A1,1 0 0,0 19,18Z" size="26" />
                    </v-avatar>
                    <div>
                        <div :class="smAndDown ? 'text-subtitle-1' : 'text-h6'" class="font-weight-bold" style="line-height: 1.2;">
                            Station météo
                        </div>
                        <div v-if="!smAndDown" class="text-caption text-medium-emphasis">Stations SenseCAP S2120</div>
                    </div>
                </div>
                <div class="d-flex align-center" style="gap: 8px;">
                    <v-btn
                        v-if="smAndDown"
                        icon="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"
                        color="primary"
                        variant="tonal"
                        size="small"
                        :loading="isLoading"
                        @click="loadDevices"
                    />
                    <v-btn
                        v-else
                        color="primary" variant="tonal" rounded="pill" prepend-icon="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"
                        :loading="isLoading" @click="loadDevices"
                    >
                        Rafraîchir
                    </v-btn>
                </div>
            </div>

            <!-- Sélecteur compact : une puce par station -->
            <div v-if="devices.length > 1" class="chips-stations mt-3">
                <v-chip
                    v-for="d in devices"
                    :key="d.id.id"
                    :color="activeId === d.id.id ? 'primary' : undefined"
                    :variant="activeId === d.id.id ? 'flat' : 'tonal'"
                    size="small"
                    prepend-icon="M12.74,5.47C15.1,6.5 16.35,9.03 15.92,11.46C17.19,12.56 18,14.19 18,16V16.17C18.31,16.06 18.65,16 19,16A3,3 0 0,1 22,19A3,3 0 0,1 19,22H6A4,4 0 0,1 2,18A4,4 0 0,1 6,14H6.27C5,12.45 4.6,10.24 5.5,8.26C6.72,5.5 9.97,4.24 12.74,5.47M11.93,7.3C10.16,6.5 8.09,7.31 7.31,9.07C6.85,10.09 6.93,11.22 7.41,12.13C8.5,10.83 10.16,10 12,10C12.7,10 13.38,10.12 14,10.34C13.94,9.06 13.18,7.86 11.93,7.3M13.55,3.64C13,3.4 12.45,3.23 11.88,3.12L14.37,1.82L15.27,4.71C14.76,4.29 14.19,3.93 13.55,3.64M6.09,4.44C5.6,4.79 5.17,5.19 4.8,5.63L4.91,2.82L7.87,3.5C7.25,3.71 6.65,4.03 6.09,4.44M18,9.71C17.91,9.12 17.78,8.55 17.59,8L19.97,9.5L17.92,11.73C18.03,11.08 18.05,10.4 18,9.71M3.04,11.3C3.11,11.9 3.24,12.47 3.43,13L1.06,11.5L3.1,9.28C3,9.93 2.97,10.61 3.04,11.3M19,18H16V16A4,4 0 0,0 12,12A4,4 0 0,0 8,16H6A2,2 0 0,0 4,18A2,2 0 0,0 6,20H19A1,1 0 0,0 20,19A1,1 0 0,0 19,18Z"
                    style="cursor: pointer; font-weight: 600;"
                    @click="onSelectDevice(d)"
                >
                    {{ d.label || d.name }}
                </v-chip>
            </div>
        </div>

        <v-divider />

        <!-- Error banner -->
        <v-alert
            v-if="loginError"
            type="error"
            variant="tonal"
            density="compact"
            class="mx-4 mt-3 mb-0 flex-shrink-0"
            rounded="lg"
        >
            {{ loginError }}
        </v-alert>

        <!-- Contenu défilable -->
        <div class="detail-scroll">

            <!-- Aucune station -->
            <div
                v-if="!isLoading && devices.length === 0 && !loginError"
                class="d-flex flex-column align-center justify-center text-medium-emphasis pa-4"
                style="height: 100%; gap: 12px;"
            >
                <v-icon icon="M12.74,5.47C15.1,6.5 16.35,9.03 15.92,11.46C17.19,12.56 18,14.19 18,16V16.17C18.31,16.06 18.65,16 19,16A3,3 0 0,1 22,19A3,3 0 0,1 19,22H6A4,4 0 0,1 2,18A4,4 0 0,1 6,14H6.27C5,12.45 4.6,10.24 5.5,8.26C6.72,5.5 9.97,4.24 12.74,5.47M11.93,7.3C10.16,6.5 8.09,7.31 7.31,9.07C6.85,10.09 6.93,11.22 7.41,12.13C8.5,10.83 10.16,10 12,10C12.7,10 13.38,10.12 14,10.34C13.94,9.06 13.18,7.86 11.93,7.3M13.55,3.64C13,3.4 12.45,3.23 11.88,3.12L14.37,1.82L15.27,4.71C14.76,4.29 14.19,3.93 13.55,3.64M6.09,4.44C5.6,4.79 5.17,5.19 4.8,5.63L4.91,2.82L7.87,3.5C7.25,3.71 6.65,4.03 6.09,4.44M18,9.71C17.91,9.12 17.78,8.55 17.59,8L19.97,9.5L17.92,11.73C18.03,11.08 18.05,10.4 18,9.71M3.04,11.3C3.11,11.9 3.24,12.47 3.43,13L1.06,11.5L3.1,9.28C3,9.93 2.97,10.61 3.04,11.3M19,18H16V16A4,4 0 0,0 12,12A4,4 0 0,0 8,16H6A2,2 0 0,0 4,18A2,2 0 0,0 6,20H19A1,1 0 0,0 20,19A1,1 0 0,0 19,18Z" size="48" />
                <span class="text-body-2 text-center">
                    Contactez-nous pour avoir une station météo sur votre ferme !
                    <a href="https://www.agrimetrie.com" target="_blank" rel="noopener noreferrer">
                        Cliquez ici pour plus d'informations.
                    </a>
                </span>
            </div>

            <template v-else-if="selectedDevice">

                <!-- ── Héro : conditions actuelles ──────────────────────────── -->
                <v-card class="hero-card" rounded="xl" flat :style="{ background: héroCondition.dégradé }">
                    <v-card-text :class="smAndDown ? 'pa-4' : 'pa-5'">
                        <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                            <div class="text-subtitle-1 font-weight-bold text-truncate" style="min-width: 0;">
                                {{ selectedDevice.label || selectedDevice.name }}
                            </div>
                            <v-btn
                                v-if="peutRenommer"
                                icon="M14.06,9L15,9.94L5.92,19H5V18.08L14.06,9M17.66,3C17.41,3 17.15,3.1 16.96,3.29L15.13,5.12L18.88,8.87L20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18.17,3.09 17.92,3 17.66,3M14.06,6.19L3,17.25V21H6.75L17.81,9.94L14.06,6.19Z"
                                variant="text"
                                size="x-small"
                                class="btn-hero"
                                @click="openRenameDialog"
                            />
                            <v-spacer />
                            <div v-if="!isLoadingTelemetry" class="d-flex align-center flex-wrap" style="gap: 6px;">
                                <v-chip
                                    v-if="latest.batteriePct !== null"
                                    size="x-small"
                                    class="chip-hero"
                                    :prepend-icon="latest.batteriePct >= 60 ? 'M16 20H8V6H16M16.67 4H15V2H9V4H7.33C6.6 4 6 4.6 6 5.33V20.67C6 21.4 6.6 22 7.33 22H16.67C17.41 22 18 21.41 18 20.67V5.33C18 4.6 17.4 4 16.67 4M15 16H9V19H15V16M15 7H9V10H15V7M15 11.5H9V14.5H15V11.5Z' : latest.batteriePct >= 30 ? 'M16 20H8V6H16M16.67 4H15V2H9V4H7.33C6.6 4 6 4.6 6 5.33V20.67C6 21.4 6.6 22 7.33 22H16.67C17.41 22 18 21.41 18 20.67V5.33C18 4.6 17.4 4 16.67 4M15 16H9V19H15V16M15 11.5H9V14.5H15V11.5Z' : 'M16 20H8V6H16M16.67 4H15V2H9V4H7.33C6.6 4 6 4.6 6 5.33V20.67C6 21.4 6.6 22 7.33 22H16.67C17.41 22 18 21.41 18 20.67V5.33C18 4.6 17.4 4 16.67 4M15 16H9V19H15V16'"
                                >
                                    {{ latest.batteriePct.toFixed(0) }} %
                                </v-chip>
                                <v-chip
                                    v-if="latest.rssi !== null"
                                    size="x-small"
                                    class="chip-hero"
                                    :prepend-icon="latest.rssi >= -70 ? 'M21,4H16V20H21V4M14,9H9V20H14V9M7,14H2V20H7V14Z' : latest.rssi >= -85 ? 'M19.5,5.5V18.5H17.5V5.5H19.5M21,4H16V20H21V4M14,9H9V20H14V9M7,14H2V20H7V14Z' : 'M19.5,5.5V18.5H17.5V5.5H19.5M12.5,10.5V18.5H10.5V10.5H12.5M21,4H16V20H21V4M14,9H9V20H14V9M7,14H2V20H7V14Z'"
                                >
                                    {{ latest.rssi }} dBm
                                </v-chip>
                                <v-chip
                                    v-if="latest.snr !== null"
                                    size="x-small"
                                    class="chip-hero"
                                    prepend-icon="M16.5,21C13.5,21 12.31,16.76 11.05,12.28C10.14,9.04 9,5 7.5,5C4.11,5 4,11.93 4,12H2C2,11.63 2.06,3 7.5,3C10.5,3 11.71,7.25 12.97,11.74C13.83,14.8 15,19 16.5,19C19.94,19 20.03,12.07 20.03,12H22.03C22.03,12.37 21.97,21 16.5,21Z"
                                >
                                    {{ latest.snr.toFixed(1) }} dB
                                </v-chip>
                            </div>
                            <v-progress-circular v-else size="16" width="2" indeterminate color="white" />
                        </div>

                        <div class="d-flex align-center flex-wrap mt-2" :style="{ gap: smAndDown ? '16px' : '28px' }">
                            <div class="d-flex align-center" style="gap: 12px;">
                                <v-icon :icon="héroCondition.icône" :size="smAndDown ? 44 : 56" />
                                <div>
                                    <div :class="smAndDown ? 'text-h3' : 'text-h2'" class="font-weight-bold" style="line-height: 1;">
                                        {{ latest.temp !== null ? latest.temp.toFixed(1) : '—' }}<span class="text-h5">°C</span>
                                    </div>
                                    <div class="texte-hero-secondaire text-body-2 mt-1">{{ héroCondition.texte }}</div>
                                </div>
                            </div>

                            <div class="d-flex flex-wrap" :style="{ gap: smAndDown ? '12px 20px' : '12px 32px' }">
                                <div class="stat-hero">
                                    <div class="texte-hero-secondaire text-caption">Humidité</div>
                                    <div class="text-subtitle-1 font-weight-bold">
                                        {{ latest.hum !== null ? `${latest.hum.toFixed(0)} %` : '—' }}
                                    </div>
                                </div>
                                <div class="stat-hero">
                                    <div class="texte-hero-secondaire text-caption">Vent</div>
                                    <div class="text-subtitle-1 font-weight-bold">
                                        {{ msVersKmh(latest.ventMs) !== null ? `${msVersKmh(latest.ventMs)!.toFixed(1)} km/h` : '—' }}
                                        <span v-if="latest.dirDeg !== null" class="texte-hero-secondaire text-caption">
                                            {{ cardinal(latest.dirDeg) }}
                                        </span>
                                    </div>
                                </div>
                                <div class="stat-hero">
                                    <div class="texte-hero-secondaire text-caption">Pluie actuelle</div>
                                    <div class="text-subtitle-1 font-weight-bold">
                                        {{ latest.tauxPrécip !== null ? `${latest.tauxPrécip.toFixed(1)} mm/h` : '—' }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="texte-hero-secondaire text-caption mt-3">
                            <v-icon icon="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z" size="12" class="me-1" />
                            <template v-if="latest.ts">
                                Dernière donnée il y a {{ tempsDepuisDernière }} — {{ new Date(latest.ts).toLocaleString('fr-CA') }}
                            </template>
                            <template v-else>En attente de données…</template>
                        </div>
                    </v-card-text>
                </v-card>

                <!-- ── Précipitations ───────────────────────────────────────── -->
                <div class="d-flex align-center mt-5 mb-2" style="gap: 8px;">
                    <v-icon icon="M9,12C9.53,12.14 9.85,12.69 9.71,13.22L8.41,18.05C8.27,18.59 7.72,18.9 7.19,18.76C6.65,18.62 6.34,18.07 6.5,17.54L7.78,12.71C7.92,12.17 8.47,11.86 9,12M13,12C13.53,12.14 13.85,12.69 13.71,13.22L11.64,20.95C11.5,21.5 10.95,21.8 10.41,21.66C9.88,21.5 9.56,20.97 9.7,20.43L11.78,12.71C11.92,12.17 12.47,11.86 13,12M17,12C17.53,12.14 17.85,12.69 17.71,13.22L16.41,18.05C16.27,18.59 15.72,18.9 15.19,18.76C14.65,18.62 14.34,18.07 14.5,17.54L15.78,12.71C15.92,12.17 16.47,11.86 17,12M17,10V9A5,5 0 0,0 12,4C9.5,4 7.45,5.82 7.06,8.19C6.73,8.07 6.37,8 6,8A3,3 0 0,0 3,11C3,12.11 3.6,13.08 4.5,13.6V13.59C5,13.87 5.14,14.5 4.87,14.96C4.59,15.43 4,15.6 3.5,15.32V15.33C2,14.47 1,12.85 1,11A5,5 0 0,1 6,6C7,3.65 9.3,2 12,2C15.43,2 18.24,4.66 18.5,8.03L19,8A4,4 0 0,1 23,12C23,13.5 22.2,14.77 21,15.46V15.46C20.5,15.73 19.91,15.57 19.63,15.09C19.36,14.61 19.5,14 20,13.72V13.73C20.6,13.39 21,12.74 21,12A2,2 0 0,0 19,10H17Z" size="20" color="primary" />
                    <span class="text-subtitle-1 font-weight-bold">Précipitations</span>
                </div>

                <!-- Tuiles de cumuls -->
                <div class="grille-pluie">
                    <v-card v-for="tuile in tuilesPluie" :key="tuile.label" variant="tonal" color="primary" rounded="xl" class="tuile-pluie">
                        <v-card-text class="pa-3">
                            <div class="d-flex align-center text-caption" style="gap: 6px; opacity: 0.85;">
                                <v-icon :icon="tuile.icône" size="14" />
                                {{ tuile.label }}
                            </div>
                            <div class="text-h5 font-weight-bold mt-1" style="line-height: 1.1;">
                                <template v-if="(isLoadingPluie || isLoadingSaison) && tuile.valeur === null">
                                    <v-progress-circular size="18" width="2" indeterminate />
                                </template>
                                <template v-else>
                                    {{ tuile.valeur !== null ? tuile.valeur.toFixed(1) : '—' }}
                                    <span class="text-body-2" style="opacity: 0.7;">mm</span>
                                </template>
                            </div>
                        </v-card-text>
                    </v-card>
                </div>

                <!-- Graphique barres journalières + cumul -->
                <v-card variant="outlined" rounded="xl" class="mt-3">
                    <v-card-title class="py-2 px-4">
                        <div class="d-flex align-center flex-wrap" style="gap: 10px;">
                            <div v-if="!smAndDown" class="d-flex align-center" style="gap: 8px; flex: 0 0 auto;">
                                <v-icon icon="M22,21H2V3H4V19H6V10H10V19H12V6H16V19H18V14H22V21Z" size="18" />
                                <span class="text-subtitle-2">Historique de pluie</span>
                            </div>
                            <div class="d-flex align-center" style="gap: 4px;">
                                <v-btn
                                    size="x-small" rounded="pill"
                                    :variant="périodePluie === '7j' ? 'flat' : 'tonal'"
                                    :color="périodePluie === '7j' ? 'primary' : undefined"
                                    @click="appliquerPériodePluie('7j')"
                                >7 j</v-btn>
                                <v-btn
                                    size="x-small" rounded="pill"
                                    :variant="périodePluie === '30j' ? 'flat' : 'tonal'"
                                    :color="périodePluie === '30j' ? 'primary' : undefined"
                                    @click="appliquerPériodePluie('30j')"
                                >30 j</v-btn>
                                <v-btn
                                    size="x-small" rounded="pill"
                                    :variant="périodePluie === 'saison' ? 'flat' : 'tonal'"
                                    :color="périodePluie === 'saison' ? 'primary' : undefined"
                                    @click="appliquerPériodePluie('saison')"
                                >Saison</v-btn>
                            </div>
                            <v-date-input
                                v-model="plagePluie"
                                label="Période"
                                multiple="range"
                                variant="outlined"
                                density="compact"
                                rounded="lg"
                                hide-details
                                autocomplete="off"
                                prepend-icon=""
                                prepend-inner-icon="M7 11H9V13H7V11M21 5V19C21 20.11 20.11 21 19 21H5C3.89 21 3 20.1 3 19V5C3 3.9 3.9 3 5 3H6V1H8V3H16V1H18V3H19C20.11 3 21 3.9 21 5M5 7H19V5H5V7M19 19V9H5V19H19M15 13H17V11H15V13M11 13H13V11H11V13Z"
                                :picker-props="{ color: 'primary' }"
                                style="min-width: 190px; flex: 1 1 190px; max-width: 300px;"
                            />
                            <v-spacer v-if="!smAndDown" />
                            <v-btn
                                icon="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"
                                variant="text"
                                size="small"
                                :loading="isLoadingPluie"
                                @click="rafraîchirPluie"
                            />
                        </div>
                    </v-card-title>
                    <v-divider />
                    <v-card-text class="pa-2">
                        <div v-if="isLoadingPluie" class="d-flex align-center justify-center chart-zone">
                            <v-progress-circular indeterminate color="primary" />
                        </div>
                        <div
                            v-else-if="pluieAffichée.length === 0"
                            class="d-flex align-center justify-center text-medium-emphasis text-caption chart-zone"
                        >
                            Aucune donnée sur la période sélectionnée
                        </div>
                        <VChart
                            v-else
                            :option="pluieChartOption"
                            autoresize
                            class="chart-zone"
                            style="width: 100%;"
                        />
                    </v-card-text>
                </v-card>

                <!-- ── Autres mesures ───────────────────────────────────────── -->
                <div class="d-flex align-center mt-5 mb-2" style="gap: 8px;">
                    <v-icon icon="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12C20,14.4 19,16.5 17.3,18C15.9,16.7 14,16 12,16C10,16 8.2,16.7 6.7,18C5,16.5 4,14.4 4,12A8,8 0 0,1 12,4M14,5.89C13.62,5.9 13.26,6.15 13.1,6.54L11.81,9.77L11.71,10C11,10.13 10.41,10.6 10.14,11.26C9.73,12.29 10.23,13.45 11.26,13.86C12.29,14.27 13.45,13.77 13.86,12.74C14.12,12.08 14,11.32 13.57,10.76L13.67,10.5L14.96,7.29L14.97,7.26C15.17,6.75 14.92,6.17 14.41,5.96C14.28,5.91 14.15,5.89 14,5.89M10,6A1,1 0 0,0 9,7A1,1 0 0,0 10,8A1,1 0 0,0 11,7A1,1 0 0,0 10,6M7,9A1,1 0 0,0 6,10A1,1 0 0,0 7,11A1,1 0 0,0 8,10A1,1 0 0,0 7,9M17,9A1,1 0 0,0 16,10A1,1 0 0,0 17,11A1,1 0 0,0 18,10A1,1 0 0,0 17,9Z" size="20" color="primary" />
                    <span class="text-subtitle-1 font-weight-bold">Mesures</span>
                    <span class="text-caption text-medium-emphasis">— touchez une tuile pour l'historique</span>
                </div>

                <v-row dense>
                    <v-col v-for="m in MÉTRIQUES" :key="m.id" cols="6" sm="4" md="3">
                        <v-card
                            variant="outlined"
                            rounded="xl"
                            class="tuile-metrique"
                            :class="{ 'tuile-active': métriqueSélectionnée === m.id }"
                            :style="métriqueSélectionnée === m.id ? { borderColor: m.couleur } : undefined"
                            @click="surClicTuile(m.id)"
                        >
                            <v-card-text class="pa-3 d-flex align-center" style="gap: 10px;">
                                <v-avatar size="34" rounded="lg" :style="{ background: `${m.couleur}22` }">
                                    <v-icon :icon="m.icône" size="18" :style="{ color: m.couleur }" />
                                </v-avatar>
                                <div style="min-width: 0;">
                                    <div class="text-caption text-medium-emphasis text-truncate">{{ m.label }}</div>
                                    <div class="text-subtitle-1 font-weight-bold" style="line-height: 1.1;">
                                        {{ valeurMétrique(m.id) !== null ? valeurMétrique(m.id)!.toFixed(m.décimales) : '—' }}
                                        <span class="text-caption text-medium-emphasis">{{ m.unité }}</span>
                                    </div>
                                    <div v-if="m.id === 'vent' && latest.dirDeg !== null" class="text-caption text-medium-emphasis">
                                        {{ cardinal(latest.dirDeg) }} ({{ latest.dirDeg.toFixed(0) }}°)
                                    </div>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Graphique de la mesure sélectionnée -->
                <v-expand-transition>
                    <v-card v-if="métriqueMeta" variant="outlined" rounded="xl" class="mt-3">
                        <v-card-title class="py-2 px-4">
                            <div class="d-flex align-center flex-wrap" style="gap: 10px;">
                                <div class="d-flex align-center" style="gap: 8px;">
                                    <v-icon :icon="métriqueMeta.icône" size="18" :style="{ color: métriqueMeta.couleur }" />
                                    <span class="text-subtitle-2">{{ métriqueMeta.label }}</span>
                                </div>
                                <div class="d-flex align-center" style="gap: 4px;">
                                    <v-btn
                                        size="x-small" rounded="pill"
                                        :variant="métriquePériodeJours === 1 ? 'flat' : 'tonal'"
                                        :color="métriquePériodeJours === 1 ? 'primary' : undefined"
                                        @click="appliquerPériodeMétrique(1)"
                                    >24 h</v-btn>
                                    <v-btn
                                        size="x-small" rounded="pill"
                                        :variant="métriquePériodeJours === 7 ? 'flat' : 'tonal'"
                                        :color="métriquePériodeJours === 7 ? 'primary' : undefined"
                                        @click="appliquerPériodeMétrique(7)"
                                    >7 j</v-btn>
                                    <v-btn
                                        size="x-small" rounded="pill"
                                        :variant="métriquePériodeJours === 30 ? 'flat' : 'tonal'"
                                        :color="métriquePériodeJours === 30 ? 'primary' : undefined"
                                        @click="appliquerPériodeMétrique(30)"
                                    >30 j</v-btn>
                                </div>
                                <v-spacer />
                                <v-btn icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" variant="text" size="small" @click="métriqueSélectionnée = null" />
                            </div>
                        </v-card-title>
                        <v-divider />
                        <v-card-text class="pa-2">
                            <div v-if="isLoadingMétrique" class="d-flex align-center justify-center chart-zone">
                                <v-progress-circular indeterminate color="primary" />
                            </div>
                            <div
                                v-else-if="métriquePoints.length === 0"
                                class="d-flex align-center justify-center text-medium-emphasis text-caption chart-zone"
                            >
                                Aucune donnée sur la période sélectionnée
                            </div>
                            <VChart
                                v-else
                                :option="métriqueChartOption"
                                autoresize
                                class="chart-zone"
                                style="width: 100%;"
                            />
                        </v-card-text>
                    </v-card>
                </v-expand-transition>

            </template>
        </div>
    </v-card>

    <!-- Renommer une station -->
    <v-dialog v-model="renameDialog" max-width="480" :fullscreen="smAndDown">
        <v-card rounded="xl">
            <v-card-title class="d-flex align-center pt-4 px-5" style="gap: 10px;">
                <v-icon icon="M14.06,9L15,9.94L5.92,19H5V18.08L14.06,9M17.66,3C17.41,3 17.15,3.1 16.96,3.29L15.13,5.12L18.88,8.87L20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18.17,3.09 17.92,3 17.66,3M14.06,6.19L3,17.25V21H6.75L17.81,9.94L14.06,6.19Z" size="20" color="primary" />
                <span class="text-subtitle-1 font-weight-bold">Renommer la station</span>
                <v-spacer />
                <v-btn icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" variant="text" size="small" @click="renameDialog = false" />
            </v-card-title>
            <v-divider />
            <v-card-text class="pt-4">
                <v-text-field
                    v-model="renameLabel"
                    label="Nom affiché"
                    variant="outlined"
                    density="comfortable"
                    rounded="lg"
                    autofocus
                    counter="255"
                    :hint="`Identifiant technique : ${selectedDevice?.name ?? ''}`"
                    persistent-hint
                    @keyup.enter="saveLabel"
                />
            </v-card-text>
            <v-card-actions class="px-5 pb-4">
                <v-spacer />
                <v-btn variant="text" rounded="pill" @click="renameDialog = false">Annuler</v-btn>
                <v-btn
                    color="primary" variant="flat" rounded="pill"
                    :loading="isSavingLabel"
                    :disabled="renameLabel.trim().length === 0"
                    @click="saveLabel"
                >
                    Enregistrer
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
    background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
    flex-shrink: 0;
}

/* Comme la page Carte : la page occupe tout le viewport, sans marge ni coins arrondis. */
.page-card {
    height: calc(100vh - var(--v-layout-top, 64px) - var(--v-layout-bottom, 0px));
    display: flex;
    flex-direction: column;
    border-radius: 0;
}

.detail-scroll {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 16px;
    min-height: 0;
}

@media (max-width: 600px) {
    .detail-scroll {
        padding: 12px;
    }
}

/* ── Puces de stations ───────────────────── */
.chips-stations {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

@media (max-width: 600px) {
    .chips-stations {
        flex-wrap: nowrap;
        overflow-x: auto;
        scrollbar-width: none;
        padding-bottom: 2px;
    }
    .chips-stations::-webkit-scrollbar {
        display: none;
    }
    .chips-stations .v-chip {
        flex: 0 0 auto;
    }
}

/* ── Héro ────────────────────────────────── */
.hero-card {
    color: #fff;
}

.texte-hero-secondaire {
    color: rgba(255, 255, 255, 0.78);
}

.chip-hero {
    background: rgba(255, 255, 255, 0.18);
    color: #fff;
}

.btn-hero {
    color: rgba(255, 255, 255, 0.85);
}

.stat-hero {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 80px;
}

/* ── Tuiles ──────────────────────────────── */
.tuile-metrique {
    cursor: pointer;
    transition: transform 0.12s, border-color 0.12s;
    height: 100%;
}
.tuile-metrique:hover {
    transform: translateY(-2px);
}
.tuile-active {
    border-width: 2px;
}

.tuile-pluie {
    height: 100%;
}

.grille-pluie {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 8px;
}

/* ── Graphiques ──────────────────────────── */
.chart-zone {
    height: 300px;
}

@media (max-width: 600px) {
    .chart-zone {
        height: 240px;
    }
}
</style>
